import type {
  EvidenceItem,
  MediaPreview,
  VillageReferenceRecord,
  VillageSummary,
  VillageViewModel,
} from "../domain/contracts";
import { matchReferenceRecord } from "./referenceData";
import { buildEvidencePreviewLabel } from "./evidenceLabels";

export function mergeVillageViewModels(villages: VillageSummary[], reference: VillageReferenceRecord[]): VillageViewModel[] {
  const useReferencePrimary = reference.length > 0 && (villages.length === 0 || villages.every(hasZeroCoordinates));

  if (useReferencePrimary) {
    const consumedPreviewVillageIds = new Set<string>();

    return reference.map((referenceRecord) => {
      const matchedVillage = villages.find((village) => {
        if (consumedPreviewVillageIds.has(village.id)) {
          return false;
        }

        const match = matchReferenceRecord(village.names.ro, village.slug, [referenceRecord]);
        return Boolean(match);
      });

      if (!matchedVillage) {
        return {
          id: referenceRecord.id,
          slug: referenceRecord.slug,
          backendRef: null,
          dataSource: "reference-only",
          title: referenceRecord.title,
          township: referenceRecord.township || "unknown",
          coordinates: {
            lat: referenceRecord.lat,
            lng: referenceRecord.lng,
          },
          stats: emptyStats(),
          availability: {
            sitesGallery: false,
            satellite: false,
            interviewees: false,
          },
          updatedAt: referenceRecord.dateAdded || referenceRecord.published || "",
          publishVersion: referenceRecord.template || "csv-reference",
          reference: referenceRecord,
        };
      }

      consumedPreviewVillageIds.add(matchedVillage.id);

      return toVillageViewModel(matchedVillage, reference);
    });
  }

  return villages.map((village) => toVillageViewModel(village, reference));
}

export function toVillageViewModel(village: VillageSummary, reference: VillageReferenceRecord[]): VillageViewModel {
  const matchedReference = matchReferenceRecord(village.names.ro, village.slug, reference);
  const township = village.township && village.township.trim() !== "" && village.township.trim().toLowerCase() !== "unknown"
    ? village.township
    : matchedReference?.township || village.township;

  return {
    id: village.id,
    slug: village.slug,
    backendRef: village.id,
    dataSource: "preview-backed",
    title: matchedReference?.title ?? village.names.en ?? village.names.ro,
    township,
    coordinates: {
      lat: matchedReference?.lat ?? village.coordinates.lat,
      lng: matchedReference?.lng ?? village.coordinates.lng,
    },
    stats: village.stats,
    availability: village.availability,
    updatedAt: village.updatedAt,
    publishVersion: village.publishVersion,
    reference: matchedReference,
  };
}

function hasZeroCoordinates(village: VillageSummary): boolean {
  return village.coordinates.lat === 0 && village.coordinates.lng === 0;
}

function emptyStats(): VillageSummary["stats"] {
  return {
    interviewCount: 0,
    officialDocumentCount: 0,
    survivorAccountCount: 0,
    reportedDeceasedCount: 0,
    reportedMissingCount: 0,
    siteMediaCount: 0,
    massGraveEvidenceCount: 0,
    evidenceItemCount: 0,
  };
}

export function choosePlaceholder(mediaType: string): string {
  if (mediaType === "audio") {
    return "/audio-thumbnail.png";
  }
  if (mediaType === "document") {
    return "/document-icon.png";
  }
  return "/village-icon.png";
}

export function buildMediaPreviewItems(evidence: EvidenceItem[], objectUrls: Map<string, string>, limit = 6): MediaPreview[] {
  return evidence.slice(0, limit).map((item) => ({
    evidenceId: item.id,
    label: buildEvidencePreviewLabel(item),
    mediaType: item.mediaType,
    mimeType: item.mimeType,
    objectUrl: objectUrls.get(item.assetId) ?? null,
    placeholder: choosePlaceholder(item.mediaType),
  }));
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatDateLabel(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}